import React, { useState } from "react";

/* ---------------------------------- Verified Data from Results Aggregator ---------------------------------- */
const DOMAIN_SUMMARY = [
  { domain: "GRC", current: 1.60, target: 3.02, gap: 1.42, ppt: { people: 37.8, process: 36.5, tech: 25.7 } },
  { domain: "People", current: 1.70, target: 3.12, gap: 1.42, ppt: { people: 33.6, process: 35.5, tech: 30.9 } },
  { domain: "Data", current: 1.48, target: 3.09, gap: 1.61, ppt: { people: 49.1, process: 27.9, tech: 23.0 } },
  { domain: "Application", current: 1.42, target: 2.85, gap: 1.43, ppt: { people: 48.8, process: 33.8, tech: 17.4 } },
  { domain: "Infrastructure", current: 2.19, target: 3.52, gap: 1.33, ppt: { people: 29.9, process: 22.3, tech: 47.8 } },
];

const TOP_CAPABILITIES = [
  { domain: "GRC", name: "Governance Structure", current: 1.50, target: 2.67, votes: [3, 3, 0, 0, 0] },
  { domain: "GRC", name: "Information Security Policy", current: 2.00, target: 3.33, votes: [0, 6, 0, 0, 0] },
  { domain: "People", name: "Identity Establishment", current: 1.67, target: 3.33, votes: [3, 2, 1, 0, 0] },
  { domain: "People", name: "Remote Access To Corporate Data", current: 2.00, target: 3.00, votes: [1, 4, 1, 0, 0] },
  { domain: "Data", name: "Data at Rest", current: 2.00, target: 3.50, votes: [0, 6, 0, 0, 0] },
  { domain: "Application", name: "Application Inventory", current: 2.17, target: 3.50, votes: [0, 5, 1, 0, 0] },
  { domain: "Infrastructure", name: "Network Security Infrastructure", current: 2.50, target: 3.83, votes: [0, 3, 3, 0, 0] },
  { domain: "Infrastructure", name: "Patch Management", current: 2.33, target: 3.83, votes: [1, 2, 3, 0, 0] },
];

/* ---------------------------------- SVG Helper Functions ---------------------------------- */
function polarToCartesian(centerX, centerY, radius, angleInDegrees) {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
}

function describeArc(x, y, radius, startAngle, endAngle) {
  const start = polarToCartesian(x, y, radius, endAngle);
  const end = polarToCartesian(x, y, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
  return ["M", start.x, start.y, "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y].join(" ");
}

/* ---------------------------------- Component 1: SVG Pie Chart ---------------------------------- */
function ResourcePieChart({ ppt }) {
  const slices = [
    { label: "People", value: ppt.people, color: "#6FA8DC" },
    { label: "Process", value: ppt.process, color: "#B48EAD" },
    { label: "Technology", value: ppt.tech, color: "#3FB88B" },
  ];

  const total = slices.reduce((acc, s) => acc + s.value, 0) || 100;
  let cumulativeAngle = 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
      <svg width={180} height={180} viewBox="-1 -1 2 2" style={{ transform: "rotate(-90deg)", overflow: "visible" }}>
        {slices.map((slice, idx) => {
          const startPercent = cumulativeAngle / total;
          cumulativeAngle += slice.value;
          const endPercent = cumulativeAngle / total;

          const startAngle = startPercent * 360;
          const endAngle = endPercent * 360;

          const startPos = polarToCartesian(0, 0, 1, startAngle);
          const endPos = polarToCartesian(0, 0, 1, endAngle);
          const largeArc = endAngle - startAngle > 180 ? 1 : 0;

          const pathData = `M ${startPos.x} ${startPos.y} A 1 1 0 ${largeArc} 1 ${endPos.x} ${endPos.y} L 0 0 Z`;

          return <path key={idx} d={pathData} fill={slice.color} stroke="#111A2C" strokeWidth="0.02" />;
        })}
      </svg>

      {/* Legend */}
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "12px", marginTop: "16px", fontSize: "11px" }}>
        {slices.map((s) => (
          <span key={s.label} style={{ color: s.color, fontWeight: "500" }}>
            ■ {s.label} ({s.value.toFixed(1)}%)
          </span>
        ))}
      </div>
    </div>
  );
}

/* ---------------------------------- Component 2: Radar Chart ---------------------------------- */
function RadarChart({ data }) {
  const size = 220;
  const center = size / 2;
  const radius = 75;
  const numAxes = data.length;
  const angleStep = 360 / numAxes;

  const getCoordinates = (value, index) => {
    const angle = index * angleStep;
    const r = (value / 5) * radius;
    return polarToCartesian(center, center, r, angle);
  };

  const currentPoints = data.map((d, i) => getCoordinates(d.current, i)).map((p) => `${p.x},${p.y}`).join(" ");
  const targetPoints = data.map((d, i) => getCoordinates(d.target, i)).map((p) => `${p.x},${p.y}`).join(" ");

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {[1, 2, 3, 4, 5].map((level) => {
          const r = (level / 5) * radius;
          const points = data
            .map((_, i) => polarToCartesian(center, center, r, i * angleStep))
            .map((p) => `${p.x},${p.y}`)
            .join(" ");
          return <polygon key={level} points={points} fill="none" stroke="#25314C" strokeWidth="1" strokeDasharray="2 2" />;
        })}

        {data.map((_, i) => {
          const p = polarToCartesian(center, center, radius, i * angleStep);
          return <line key={i} x1={center} y1={center} x2={p.x} y2={p.y} stroke="#25314C" strokeWidth="1" />;
        })}

        <polygon points={targetPoints} fill="rgba(63, 184, 139, 0.15)" stroke="#3FB88B" strokeWidth="2" />
        <polygon points={currentPoints} fill="rgba(232, 145, 45, 0.3)" stroke="#E8912D" strokeWidth="2" />

        {data.map((d, i) => {
          const labelPos = polarToCartesian(center, center, radius + 18, i * angleStep);
          return (
            <text
              key={d.domain}
              x={labelPos.x}
              y={labelPos.y}
              fill="#E8ECF6"
              fontSize="9"
              fontWeight="bold"
              textAnchor="middle"
              alignmentBaseline="middle"
            >
              {d.domain}
            </text>
          );
        })}
      </svg>
      <div style={{ display: "flex", gap: "16px", marginTop: "8px", fontSize: "11px" }}>
        <span style={{ color: "#E8912D" }}>■ Current Posture</span>
        <span style={{ color: "#3FB88B" }}>■ Target Posture</span>
      </div>
    </div>
  );
}

/* ---------------------------------- Main Analytics Component ---------------------------------- */
export default function GRCResultGraphs() {
  const [selectedDomain, setSelectedDomain] = useState("ALL");

  const filteredSummary = selectedDomain === "ALL"
    ? DOMAIN_SUMMARY
    : DOMAIN_SUMMARY.filter((d) => d.domain === selectedDomain);

  const filteredCapabilities = selectedDomain === "ALL"
    ? TOP_CAPABILITIES
    : TOP_CAPABILITIES.filter((c) => c.domain === selectedDomain);

  // Compute aggregated PPT ratio for Pie Chart
  const aggregatePPT = selectedDomain === "ALL"
    ? DOMAIN_SUMMARY.reduce(
        (acc, curr) => ({
          people: acc.people + curr.ppt.people / DOMAIN_SUMMARY.length,
          process: acc.process + curr.ppt.process / DOMAIN_SUMMARY.length,
          tech: acc.tech + curr.ppt.tech / DOMAIN_SUMMARY.length,
        }),
        { people: 0, process: 0, tech: 0 }
      )
    : filteredSummary[0].ppt;

  return (
    <div className="analytics-container" style={{ padding: "24px", maxWidth: "1280px", margin: "0 auto", fontFamily: "sans-serif", color: "#E8ECF6" }}>
      {/* Responsive Styles Injection */}
      <style>{`
        .charts-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        }
        .span-2 {
          grid-column: span 2;
        }
        .capabilities-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }
        @media (max-width: 768px) {
          .charts-grid {
            grid-template-columns: 1fr !important;
          }
          .span-2 {
            grid-column: span 1 !important;
          }
          .capabilities-grid {
            grid-template-columns: 1fr !important;
          }
          .analytics-container {
            padding: 12px !important;
          }
        }
      `}</style>

      {/* Header & Controls */}
      <div style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
        <div>
          <h1 style={{ fontSize: "22px", margin: "0 0 4px 0", color: "#E8ECF6" }}>
            📈 Easy-to-Understand Security Analytics
          </h1>
          <p style={{ color: "#8B96B4", fontSize: "12px", margin: 0 }}>
            Source: <code>Results Aggregator Sheet</code> — IBM ESMW Security Workshop Data
          </p>
        </div>

        {/* Filter Dropdown */}
        <select
          value={selectedDomain}
          onChange={(e) => setSelectedDomain(e.target.value)}
          style={{
            backgroundColor: "#16213A",
            color: "#E8ECF6",
            border: "1px solid #25314C",
            padding: "8px 16px",
            borderRadius: "6px",
            fontSize: "12px",
            cursor: "pointer",
          }}
        >
          <option value="ALL">All Domains</option>
          {DOMAIN_SUMMARY.map((d) => (
            <option key={d.domain} value={d.domain}>{d.domain}</option>
          ))}
        </select>
      </div>

      {/* Grid Layout of Visual Graphs */}
      <div className="charts-grid">
        
        {/* GRAPH 1: Pie Chart (Resource Allocation) */}
        <div style={{ background: "#111A2C", border: "1px solid #25314C", borderRadius: "8px", padding: "20px" }}>
          <h3 style={{ fontSize: "14px", color: "#D4A73D", margin: "0 0 8px 0" }}>
            🥧 1. Resource Allocation (Pie View)
          </h3>
          <p style={{ fontSize: "11px", color: "#59648A", marginBottom: "16px" }}>
            {selectedDomain === "ALL" ? "Overall average resource distribution" : `${selectedDomain} domain resource split`} between People, Process, and Tech.
          </p>
          <ResourcePieChart ppt={aggregatePPT} />
        </div>

        {/* GRAPH 2: SVG Radar / Spider Chart */}
        <div style={{ background: "#111A2C", border: "1px solid #25314C", borderRadius: "8px", padding: "20px" }}>
          <h3 style={{ fontSize: "14px", color: "#D4A73D", margin: "0 0 8px 0" }}>
            🕸️ 2. Domain Maturity Radar Chart
          </h3>
          <p style={{ fontSize: "11px", color: "#59648A", marginBottom: "16px" }}>
            Visualizes current posture coverage (Orange area) against the target goal shape (Green outline).
          </p>
          <RadarChart data={filteredSummary} />
        </div>

        {/* GRAPH 3: Bar Progress Comparison */}
        <div style={{ background: "#111A2C", border: "1px solid #25314C", borderRadius: "8px", padding: "20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
            <h3 style={{ fontSize: "14px", color: "#E8912D", margin: 0 }}>
              📊 3. Current Score vs Target Goal
            </h3>
            <span style={{ fontSize: "11px", color: "#8B96B4" }}>Scale: 1 to 5</span>
          </div>
          <p style={{ fontSize: "11px", color: "#59648A", marginBottom: "16px" }}>
            Orange bar represents current score; green vertical marker indicates target score.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {filteredSummary.map((item) => (
              <div key={item.domain}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", marginBottom: "4px" }}>
                  <strong style={{ color: "#E8ECF6" }}>{item.domain}</strong>
                  <span>
                    Current: <strong style={{ color: "#E8912D" }}>{item.current.toFixed(2)}</strong> | Target: <strong style={{ color: "#3FB88B" }}>{item.target.toFixed(2)}</strong>
                  </span>
                </div>
                <div style={{ height: "12px", background: "#16213A", borderRadius: "6px", position: "relative", overflow: "hidden" }}>
                  <div style={{ width: `${(item.current / 5) * 100}%`, height: "100%", background: "#E8912D", borderRadius: "6px" }} />
                  <div style={{ position: "absolute", left: `${(item.target / 5) * 100}%`, top: 0, bottom: 0, width: "3px", background: "#3FB88B" }} title={`Target: ${item.target}`} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* GRAPH 4: Gap Severity Meters */}
        <div style={{ background: "#111A2C", border: "1px solid #25314C", borderRadius: "8px", padding: "20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
            <h3 style={{ fontSize: "14px", color: "#E2434A", margin: 0 }}>
              🚨 4. Security Gap Severity (Deficit)
            </h3>
            <span style={{ fontSize: "11px", color: "#E2434A", fontWeight: "bold" }}>Points Below Target</span>
          </div>
          <p style={{ fontSize: "11px", color: "#59648A", marginBottom: "16px" }}>
            Longer red bars denote larger maturity deficits requiring urgent mitigation.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {filteredSummary.map((item) => (
              <div key={item.domain}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", marginBottom: "4px" }}>
                  <strong style={{ color: "#E8ECF6" }}>{item.domain}</strong>
                  <span style={{ color: "#E2434A", fontWeight: "bold" }}>-{item.gap.toFixed(2)} Points Gap</span>
                </div>
                <div style={{ height: "12px", background: "#16213A", borderRadius: "6px", overflow: "hidden" }}>
                  <div style={{ width: `${(item.gap / 2) * 100}%`, height: "100%", background: "linear-gradient(90deg, #E8912D, #E2434A)" }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* GRAPH 5: Participant Vote Histogram */}
        <div className="span-2" style={{ background: "#111A2C", border: "1px solid #25314C", borderRadius: "8px", padding: "20px" }}>
          <h3 style={{ fontSize: "14px", color: "#E8ECF6", margin: "0 0 4px 0" }}>
            🗳️ 5. Workshop Participant Vote Distribution
          </h3>
          <p style={{ fontSize: "11px", color: "#59648A", marginBottom: "16px" }}>
            Histogram showing how 21 workshop participants voted across maturity Levels 1 to 5 for key capabilities.
          </p>

          <div className="capabilities-grid">
            {filteredCapabilities.map((cap) => (
              <div key={cap.name} style={{ background: "#16213A", border: "1px solid #25314C", borderRadius: "6px", padding: "12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "#E8ECF6", marginBottom: "6px" }}>
                  <span>{cap.name}</span>
                  <span style={{ color: "#8B96B4", fontFamily: "monospace" }}>{cap.current.toFixed(2)} → {cap.target.toFixed(2)}</span>
                </div>
                <div style={{ display: "flex", alignItems: "flex-end", gap: "6px", height: "32px", marginTop: "8px" }}>
                  {cap.votes.map((v, idx) => (
                    <div key={idx} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <div
                        style={{
                          width: "100%",
                          height: `${Math.max(v * 4, 2)}px`,
                          background: v > 0 ? "#D4A73D" : "#25314C",
                          borderRadius: "2px",
                        }}
                      />
                      <span style={{ fontSize: "9px", color: "#59648A", marginTop: "2px" }}>L{idx + 1}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}